/**
 * Power Automate Integration Settings
 *
 * The backend (PowerAutomateController) is already fully built in velaro-messaging.
 * This page gives admins a UI to configure the Flow URL and see how to use it.
 *
 * Backend endpoints (velaro-messaging):
 *   GET  /PowerAutomate/config          → { configured, displayName, flowUrlMasked }
 *   POST /PowerAutomate/config          → { flowUrl, displayName }
 *   POST /PowerAutomate/Webhook?jobId=  → triggers the flow (called by workflow engine)
 */
import React, { useEffect, useState } from "react";
import { PrimaryBlueButton, SecondaryBlueButton } from "@velaro/velaro-component-lib";
import useApi from "@/hooks/useApi";
import IntegrationActivityLog from "../IntegrationActivityLog";

interface Config {
  configured: boolean;
  displayName: string;
  flowUrlMasked: string;
}

function PowerAutomateLogo() {
  return (
    <svg viewBox="0 0 100 100" className="w-10 h-10" fill="none">
      <rect width="100" height="100" rx="18" fill="#0066FF" />
      <path d="M20 70 L50 25 L80 70" stroke="white" strokeWidth="8" strokeLinejoin="round" fill="none" />
      <path d="M35 55 L65 55" stroke="white" strokeWidth="8" strokeLinecap="round" />
    </svg>
  );
}

export default function PowerAutomatePage() {
  const api = useApi();
  const [config, setConfig] = useState<Config | null>(null);
  const [flowUrl, setFlowUrl] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.messaging.get("PowerAutomate/config")
      .then(r => r.json())
      .then((data: Config) => {
        setConfig(data);
        setDisplayName(data.displayName || "");
      })
      .catch(() => setConfig({ configured: false, displayName: "", flowUrlMasked: "" }));
  }, []);

  async function save() {
    if (!flowUrl.trim()) { setError("Flow URL is required"); return; }
    setSaving(true);
    setError("");
    try {
      const res = await api.messaging.post("PowerAutomate/config", { flowUrl, displayName });
      if (res.ok) {
        setSaved(true);
        const updated = await api.messaging.get("PowerAutomate/config").then(r => r.json());
        setConfig(updated);
        setFlowUrl("");
        setTimeout(() => setSaved(false), 3000);
      } else {
        setError("Failed to save. Check the Flow URL and try again.");
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      {/* Header */}
      <div className="font-semibold group flex items-center gap-2 justify-between mb-6">
        <div className="flex items-center gap-2">
          <div className="w-14 h-14 border-slate-300 relative mr-4 rounded border p-2 content-center flex items-center justify-center">
            <PowerAutomateLogo />
          </div>
          <div>
            <div className="font-semibold text-slate-gray-800 text-lg">Power Automate</div>
            <div className="font-normal text-slate-gray-400 text-sm">
              Trigger Microsoft Power Automate flows from chat conversations and workflow nodes.
            </div>
          </div>
        </div>
        {config?.configured && (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-3 py-1">
            <span className="w-2 h-2 rounded-full bg-green-500 inline-block" /> Connected
          </span>
        )}
      </div>

      {/* Status bar */}
      {config?.configured && (
        <div className="mb-6 rounded-lg border border-slate-200 p-4 bg-slate-50 text-sm">
          <div className="font-medium text-slate-700 mb-1">Active Configuration</div>
          <div className="text-slate-500">
            <span className="font-medium">Flow:</span> {config.displayName || "Unnamed flow"}
          </div>
          <div className="text-slate-500 font-mono text-xs mt-1">{config.flowUrlMasked}</div>
        </div>
      )}

      {/* Feature cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        {[
          {
            icon: "⚡",
            title: "Workflow Node",
            desc: "Add a Power Automate step inside any workflow. The flow receives full conversation context and can write values back."
          },
          {
            icon: "🤖",
            title: "AI Tool",
            desc: "AI chatbots can call Power Automate flows as a tool — e.g. to create tickets, send Teams messages, or update records."
          },
          {
            icon: "🔄",
            title: "Bidirectional",
            desc: "Velaro sends conversation data to your flow. Your flow can return key-value pairs that get saved as conversation variables."
          }
        ].map(f => (
          <div key={f.title} className="rounded-lg border border-slate-200 p-4">
            <div className="text-2xl mb-2">{f.icon}</div>
            <div className="font-medium text-slate-800 text-sm mb-1">{f.title}</div>
            <div className="text-xs text-slate-500">{f.desc}</div>
          </div>
        ))}
      </div>

      {/* Setup wizard */}
      <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-5">
        <div className="font-semibold text-blue-800 mb-3">Quick Setup (3 steps)</div>
        <ol className="space-y-2 text-sm text-blue-700">
          <li className="flex items-start gap-2">
            <span className="shrink-0 w-5 h-5 rounded-full bg-blue-200 text-blue-800 text-xs font-bold flex items-center justify-center">1</span>
            <span>In Power Automate, create a new flow with the <strong>When an HTTP request is received</strong> trigger.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="shrink-0 w-5 h-5 rounded-full bg-blue-200 text-blue-800 text-xs font-bold flex items-center justify-center">2</span>
            <span>Copy the <strong>HTTP POST URL</strong> from that trigger step.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="shrink-0 w-5 h-5 rounded-full bg-blue-200 text-blue-800 text-xs font-bold flex items-center justify-center">3</span>
            <span>Paste it below and save. Add a <strong>Power Automate</strong> node to any workflow to trigger the flow.</span>
          </li>
        </ol>
      </div>

      {/* Configuration form */}
      <div className="border border-slate-200 rounded-lg p-5">
        <div className="font-medium text-slate-800 mb-4">
          {config?.configured ? "Update Flow URL" : "Connect Power Automate"}
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Display Name (optional)</label>
            <input
              className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="e.g. Lead Capture Flow"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Power Automate Flow URL *</label>
            <input
              className="w-full border border-slate-300 rounded px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="https://prod-00.eastus.logic.azure.com/..."
              value={flowUrl}
              onChange={e => { setFlowUrl(e.target.value); setError(""); }}
            />
          </div>
          {error && <p className="text-xs text-red-600">{error}</p>}
          {saved && <p className="text-xs text-green-600">Saved successfully.</p>}
        </div>

        <div className="flex gap-3 mt-4">
          <PrimaryBlueButton
            label={saving ? "Saving..." : "Save"}
            onClick={save}
            prefixIcon={null}
            className="font-medium"
          />
        </div>
      </div>

      {/* Payload reference */}
      <div className="mt-6 border border-slate-200 rounded-lg p-5">
        <div className="font-medium text-slate-800 mb-3">What Velaro sends to your flow</div>
        <pre className="text-xs bg-slate-50 border border-slate-200 rounded p-3 overflow-auto text-slate-700">{`{
  "jobId": 12345,
  "siteId": 1001,
  "conversationId": 98765,
  "contactEmail": "visitor@example.com",
  "contactName": "John Smith",
  "agentName": "Sarah Lee",
  "sentiment": "positive",
  "workflowVariables": {
    "orderNumber": "ORD-1234",
    "issueType": "Billing"
  }
}`}</pre>
        <p className="text-xs text-slate-500 mt-2">
          Your flow can return a JSON object with key-value pairs — Velaro will save them as workflow variables
          available in later nodes as <code className="bg-slate-100 px-1 rounded">{"<<workflow.keyName>>"}</code>.
        </p>
      </div>

      {/* Activity Log */}
      <div className="mt-8">
        <h2 className="text-sm font-semibold text-slate-700 mb-3">Activity Log</h2>
        <IntegrationActivityLog integrationSlug="PowerAutomate" />
      </div>
    </div>
  );
}
